import { Modal, Select, TimePicker, message } from "antd"
import { useState, useEffect } from "react"
import dayjs from "dayjs"
import PanelLayout from "../../../Layout/PanelLayout"
import TableBox from "../../../components/TableBox/TableBox"
import Button from "../../../components/Button/Button"
import apiClient from "../../../Utils/apiClient"
import AutoCompleteBox from "../../../components/AutoCompleteBox/AutoCompleteBox"
import { getStoredPageSize } from "../../../Utils/TableUtils/TableUtils"
import ShutdownReminderSettings from "./ShutdownReminderSettings"
import InactivityTenantSettings from "./InactivityTenantSettings"
import "../../Operations/Operations.scss"

const deviceIsExcluded = (row = {}) =>
  row.notificationsExcluded === true || row.restrictInactivityEmail === true

const InactivityNotificationSettings = () => {
  const devicePage = "Device Notification Exclusions"
  const userPage = "User Inactivity Email"

  const [loading, setLoading] = useState(false)
  const [usersLoading, setUsersLoading] = useState(false)
  const [devices, setDevices] = useState([])
  const [users, setUsers] = useState([])
  const [filteredDevices, setFilteredDevices] = useState([])
  const [selectedDeviceKeys, setSelectedDeviceKeys] = useState([])
  const [selectedUserKeys, setSelectedUserKeys] = useState([])
  const [hoursUser, setHoursUser] = useState(null)
  const [hoursStart, setHoursStart] = useState(null)
  const [hoursEnd, setHoursEnd] = useState(null)

  const [filters, setFilters] = useState({
    hostname: "",
    user: "",
    groupedIn: "",
    notificationsExcluded: "",
  })

  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(devicePage) || 10,
    total: 0,
  })

  const [userPagination, setUserPagination] = useState({
    page: 1,
    pageSize: getStoredPageSize(userPage) || 10,
    total: 0,
  })

  const fetchDevices = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize,
    customFilters = filters
  ) => {
    setLoading(true)

    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
        hostname: customFilters.hostname,
        user: customFilters.user,
        groupedIn: customFilters.groupedIn,
        notificationsExcluded: customFilters.notificationsExcluded,
      }

      const res = await apiClient("devices", "GET", null, {
        params,
        projections: [
          "hostname",
          "user",
          "groupedIn",
          "notificationsExcluded",
          "restrictInactivityEmail",
        ],
      })

      if (res) {
        setDevices(res.data)
        setPaginationData(res.pageData)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async (
    customPage = userPagination.page,
    customPageSize = userPagination.pageSize
  ) => {
    setUsersLoading(true)
    try {
      const res = await apiClient("users", "GET", null, {
        params: { page: customPage, pageSize: customPageSize },
      })
      if (res) {
        setUsers(res.data || [])
        setUserPagination(res.pageData || { page: customPage, pageSize: customPageSize, total: 0 })
      }
    } catch (err) {
      console.error(err)
    } finally {
      setUsersLoading(false)
    }
  }

  useEffect(() => {
    fetchDevices()
    fetchUsers()
  }, [])

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination
    setPaginationData((prev) => ({
      ...prev,
      page: current,
      pageSize,
    }))
    fetchDevices(current, pageSize, filters)
  }

  const handleUserTableChange = (pagination) => {
    const { current, pageSize } = pagination
    setUserPagination((prev) => ({
      ...prev,
      page: current,
      pageSize,
    }))
    fetchUsers(current, pageSize)
  }

  const toggleDeviceNotification = async (value) => {
    if (selectedDeviceKeys.length === 0) {
      message.warning("Select devices first")
      return
    }

    try {
      const res = await apiClient("devices/toggle-notifications", "PATCH", {
        deviceIds: selectedDeviceKeys,
        notificationsExcluded: value,
      })

      if (res.success) {
        message.success(res.message)
        setSelectedDeviceKeys([])
        fetchDevices(paginationData.page, paginationData.pageSize, filters)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const toggleUserNotification = async (value) => {
    if (selectedUserKeys.length === 0) {
      message.warning("Select users first")
      return
    }
    try {
      const res = await apiClient("users/toggle-inactivity-email", "PATCH", {
        userIds: selectedUserKeys,
        restrictInactivityEmail: value,
      })
      if (res.success) {
        message.success(res.message)
        setSelectedUserKeys([])
        fetchUsers(userPagination.page, userPagination.pageSize)
      }
    } catch (err) {
      message.error(err.message || "Could not update users")
    }
  }

  const saveUserHours = async () => {
    if (!hoursUser?._id) return
    try {
      const res = await apiClient("users/toggle-inactivity-email", "PATCH", {
        userIds: [hoursUser._id],
        workStart: hoursStart ? hoursStart.format("HH:mm") : null,
        workEnd: hoursEnd ? hoursEnd.format("HH:mm") : null,
        clearWorkHours: !hoursStart && !hoursEnd,
      })
      if (res.success) {
        message.success(res.message)
        setHoursUser(null)
        fetchUsers(userPagination.page, userPagination.pageSize)
      }
    } catch (err) {
      message.error(err.message || "Could not save work hours")
    }
  }

  const deviceColumns = [
    {
      title: "Hostname",
      dataIndex: "hostname",
      key: "hostname",
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
    },
    {
      title: "Group",
      dataIndex: "groupedIn",
      key: "groupedIn",
    },
    {
      title: "Notifications",
      key: "notificationsExcluded",
      render: (_, record) =>
        deviceIsExcluded(record) ? (
          <span style={{ color: "red" }}>Excluded</span>
        ) : (
          <span style={{ color: "green" }}>Allowed</span>
        ),
    },
  ]

  const userColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Work hours",
      key: "hours",
      render: (_, record) =>
        record.workStart && record.workEnd
          ? `${record.workStart}–${record.workEnd}`
          : "Tenant default",
    },
    {
      title: "Inactivity Notification",
      dataIndex: "restrictInactivityEmail",
      key: "restrictInactivityEmail",
      render: (val) =>
        val ? (
          <span style={{ color: "red" }}>Restricted</span>
        ) : (
          <span style={{ color: "green" }}>Allowed</span>
        ),
    },
    {
      title: "",
      key: "setHours",
      width: 120,
      render: (_, record) => (
        <Button
          text="Work hours"
          onClick={() => {
            setHoursUser(record)
            setHoursStart(record.workStart ? dayjs(record.workStart, "HH:mm") : null)
            setHoursEnd(record.workEnd ? dayjs(record.workEnd, "HH:mm") : null)
          }}
        />
      ),
    },
  ]

  const handleClear = () => {
    const clearedFilters = {
      hostname: "",
      user: "",
      groupedIn: "",
      notificationsExcluded: "",
    }

    setFilters(clearedFilters)

    fetchDevices(1, paginationData.pageSize, clearedFilters)
  }

  const formatExportData = (rows) => {
    return rows?.map((d) => ({
      hostname: d.hostname,
      user: d.user,
      group: d.groupedIn,
      notificationsExcluded: deviceIsExcluded(d) ? "Excluded" : "Allowed",
    }))
  }

  const exportOptions = {
    endpoint: "filter/users",
    method: "POST",
    params: {
      pageSize: paginationData.total,
      hostname: filters?.hostname,
      user: filters?.user,
      groupedIn: filters?.groupedIn,
      notificationsExcluded: filters?.notificationsExcluded,
    },
    onExportData: (data) => setFilteredDevices(data),
    format: formatExportData,
    data: formatExportData(filteredDevices),
    header: ["Hostname", "User", "Group", "Notifications"],
    btnTitle: "Export",
    fileName: "Device Notification Exclusions",
  }

  const deviceRestricted = devices.filter((row) => deviceIsExcluded(row)).length
  const userRestricted = users.filter(
    (row) => row.restrictInactivityEmail
  ).length
  const kpis = [
    {
      key: "users",
      label: "Users shown",
      value: userPagination.total || users.length,
      tone: "#2563eb",
      soft: "rgba(59, 130, 246, 0.14)",
    },
    {
      key: "userOff",
      label: "Users muted",
      value: userRestricted,
      tone: "#d97706",
      soft: "rgba(245, 158, 11, 0.16)",
    },
    {
      key: "devices",
      label: "Devices shown",
      value: paginationData.total || devices.length,
      tone: "#0891b2",
      soft: "rgba(56, 189, 248, 0.14)",
    },
    {
      key: "deviceOff",
      label: "Devices excluded",
      value: deviceRestricted,
      tone: "#ea580c",
      soft: "rgba(234, 88, 12, 0.16)",
    },
  ]

  return (
    <PanelLayout
      title="Notifications"
      subtitle="Exclude devices from all device-scoped alerts, and configure inactivity / shutdown schedules."
    >
      <div className="ops-kpis">
        {kpis.map((card) => (
          <div
            key={card.key}
            className="ops-kpi ops-kpi--static"
            style={{ "--act-tone": card.tone, "--act-soft": card.soft }}
          >
            <div>
              <span className="ops-kpi-label">{card.label}</span>
              <strong style={{ fontSize: 17 }}>{card.value}</strong>
            </div>
          </div>
        ))}
      </div>
      <p className="ops-muted" style={{ marginTop: 0, marginBottom: 16 }}>
        Device exclusions mute inactivity emails, shutdown / overnight reminders, media
        alerts, input-integrity admin mail, and device rows in emailed fleet reports.
        Manual Excel/PDF downloads are unchanged. Security enforcement (e.g. lock) still
        runs.
      </p>
      <ShutdownReminderSettings />
      <InactivityTenantSettings />
      <TableBox
        className="dashboard-inventory-table"
        title="User inactivity email"
        columns={userColumns}
        data={users}
        loading={usersLoading}
        rowKey="_id"
        rowSelection={{
          selectedRowKeys: selectedUserKeys,
          onChange: (keys) => setSelectedUserKeys(keys),
        }}
        page={userPage}
        extra={
          <>
            <Button
              text="Enable selected users"
              onClick={() => toggleUserNotification(false)}
              disabled={selectedUserKeys.length === 0}
            />
            <Button
              text="Disable selected users"
              onClick={() => toggleUserNotification(true)}
              disabled={selectedUserKeys.length === 0}
            />
          </>
        }
        pagination={{
          current: userPagination.page,
          pageSize: userPagination.pageSize,
          total: userPagination.total,
          showSizeChanger: true,
        }}
        onChange={handleUserTableChange}
      />
      <TableBox
        className="dashboard-inventory-table"
        title="Device notification exclusions"
        columns={deviceColumns}
        data={devices}
        loading={loading}
        rowSelection={{
          selectedRowKeys: selectedDeviceKeys,
          onChange: (keys) => setSelectedDeviceKeys(keys),
        }}
        page={devicePage}
        exportable
        exportOptions={exportOptions}
        extra={
          <>
            <Button
              text="Allow notifications"
              onClick={() => toggleDeviceNotification(false)}
              disabled={selectedDeviceKeys.length === 0}
            />
            <Button
              text="Exclude from notifications"
              onClick={() => toggleDeviceNotification(true)}
              disabled={selectedDeviceKeys.length === 0}
            />
          </>
        }
        pagination={{
          current: paginationData.page,
          pageSize: paginationData.pageSize,
          total: paginationData.total,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        enableFilter
        filtercontent={
          <div className="p-3 d-flex flex-column gap-3">
            <AutoCompleteBox
              label={"Hostname"}
              name="hostname"
              className="mb-3 w-100"
              endPoint={`users`}
              placeholder="Search Host Name"
              value={filters.hostname}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, hostname: value }))
              }
            />

            <AutoCompleteBox
              label={"User Name"}
              name="user"
              className="mb-3 w-100"
              endPoint={`users`}
              placeholder="Search User Name"
              value={filters.user}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, user: value }))
              }
            />

            <AutoCompleteBox
              label={"Group"}
              name="name"
              className="mb-3 w-100"
              endPoint={`group-devices`}
              placeholder="Search Group Name"
              value={filters.groupedIn}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, groupedIn: value }))
              }
            />

            <div>
              <label>Notification status</label>
              <Select
                className="w-100"
                allowClear
                value={filters.notificationsExcluded}
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    notificationsExcluded: value,
                  }))
                }
                options={[
                  { label: "Allowed", value: "false" },
                  { label: "Excluded", value: "true" },
                ]}
              />
            </div>
          </div>
        }
        onFilter={() => {
          setPaginationData((prev) => ({ ...prev, page: 1 }))
          fetchDevices(1, paginationData.pageSize, filters)
        }}
        onClear={handleClear}
        filters={filters}
      />
      <Modal
        title={hoursUser ? `Work hours for ${hoursUser.name}` : "Work hours"}
        open={Boolean(hoursUser)}
        onCancel={() => setHoursUser(null)}
        onOk={saveUserHours}
        okText="Save hours"
      >
        <p className="user-activity__muted">
          Leave both times empty to use the tenant work-hour schedule.
        </p>
        <div className="d-flex gap-3">
          <div>
            <div className="mb-1">Starts</div>
            <TimePicker
              format="HH:mm"
              value={hoursStart}
              onChange={setHoursStart}
            />
          </div>
          <div>
            <div className="mb-1">Ends</div>
            <TimePicker format="HH:mm" value={hoursEnd} onChange={setHoursEnd} />
          </div>
        </div>
      </Modal>
    </PanelLayout>
  )
}

export default InactivityNotificationSettings
